'use client';

import {createKeyboardEventHandler} from '@components/@shared/VideoPlayer/@utils/createKeyboardEventHandler';
import {getPlayerWatchTime} from '@components/@shared/VideoPlayer/@utils/getPlayerWatchTime';
import {
    VideoJSEventEnum,
    VideoPlayerEventEnum,
} from '@components/@shared/VideoPlayer/types';
import {KeyboardInputEnum} from '@config/keyboard';
import {VideoPlayerConfig} from '@config/videoPlayer';
import {BusEventEnum, EventBus} from '@core/eventBus';
import * as Sentry from '@sentry/nextjs';
import createZustandStore from '@utils/createZustandStore';
import {
    FC,
    PropsWithChildren,
    createContext,
    useContext,
    useMemo,
    useState as useReactState,
    useRef,
} from 'react';
import Player from 'video.js/dist/types/player';
import {SourceObject} from 'video.js/dist/types/tech/tech';
import {useStore} from 'zustand';

export interface IVideoPlayerState<T = object> {
    metadata?: T;
    isReady: boolean;
    isPlaying: boolean;
    isLoading: boolean;
    isInteracting: boolean;
    poster?: string;
    isEnded: boolean;
    error: boolean;
    src: SourceObject;
    isTheaterMode?: boolean;
    isStarted: boolean;
    isScrubbing: boolean;
    isSeeking: boolean;
    isFullscreen: boolean;
    isMuted: boolean;
    isAutoplay: boolean;
    isInPip: boolean;
    volume: number;
    startFrom: number;
    currentTime: number;
    duration: number;
    qualityLevel: number;
    playbackRate: number;
    seekFrom: number;
    textTrackIndex: number;
    ccEnabled: boolean;
    areControlsVisible: boolean;
}

export const initState: IVideoPlayerState<object> = {
    src: '',
    error: false,
    isReady: false,
    isEnded: false,
    isPlaying: false,
    isLoading: false,
    isInteracting: true,
    isMuted: false,
    isStarted: false,
    isScrubbing: false,
    isSeeking: false,
    isFullscreen: false,
    volume: 1,
    startFrom: 0,
    isAutoplay: false,
    currentTime: 0,
    duration: 0,
    qualityLevel: -1,
    playbackRate: 1,
    seekFrom: 0,
    textTrackIndex: -1,
    ccEnabled: false,
    areControlsVisible: true,
    metadata: undefined,
    isInPip: false,
};

const useCreateVideoController = () => {
    const [state] = useReactState(() => {
        return createZustandStore<IVideoPlayerState>(() => {
            return initState;
        });
    });

    const listeners = useRef<
        {
            type: string;
            callback: (watchPercentage: number, currentTime: number) => void;
        }[]
    >([]);

    const playerRef = useRef<Player | null>(null);

    // tracks if the current seek came from a slider scrub, so onSeekEnd can
    // skip the duplicate native seeked event (scrubs are logged via SCRUB_END)
    const seekIsFromScrubRef = useRef(false);

    const controller = useMemo(() => {
        const player = new Proxy(playerRef, {
            get: (_, prop) => {
                if (!playerRef.current) {
                    throw Error('Player not initialized');
                }
                return playerRef.current[prop as keyof Player];
            },
        }) as unknown as Player;

        const load = ({
            source,
            autoplay,
            position,
            metadata,
        }: {
            source: SourceObject;
            autoplay?: boolean;
            position?: number;
            metadata?: any;
        }) => {
            state.setState({
                src: source,
                isAutoplay: autoplay,
                startFrom: position,
                isStarted: false,
                isMuted: player.muted() ?? false,
                error: false,
                duration: player.duration() ?? 0,
                metadata,
            });
            player.src(source);
            player.volume(state.getState().volume);
        };

        const play = async () => {
            if (!player.src(undefined)) {
                throw Error('No source to play');
            }
            if (!state.getState().isStarted && state.getState().startFrom) {
                // Normally isStarted will be set by onPlay listener, but in
                // case startFrom is at the end of video, it will cause infinite
                // loop without this state update, as seek will try to play
                // video to let it finish and enter "isEnded" state.
                state.setState({isStarted: true});
                seek(state.getState().startFrom);
            }
            player.play();
            focus();
        };

        const pause = () => {
            player.pause();
        };

        const playPause = () => {
            if (player.paused()) {
                play();
            } else {
                pause();
            }
        };

        const focus = () => {
            player.focus();
        };

        const seek = (to: number) => {
            const isScrubbing = state.getState().isScrubbing;
            seekIsFromScrubRef.current = isScrubbing;
            const {duration, currentTime} = getPlayerWatchTime(player);

            // discrete seeks (chapters, buttons) capture their origin here;
            // scrubs already set seekFrom in onScrubStart in progress slider
            if (!isScrubbing) {
                state.setState({seekFrom: currentTime});
            }

            if (to >= duration - 1) {
                const newTime = Math.floor(duration - 0.1);
                state.setState({currentTime: newTime});
                player.currentTime(newTime);
                play();
            } else {
                const newTime = Math.max(0, to);
                state.setState({currentTime: newTime});
                if (!state.getState().isStarted) {
                    // If seeked to a new time before player started for e.g., from chapters list,
                    // startFrom should be overridden for play event
                    state.setState({startFrom: newTime});
                }
                player.currentTime(newTime);
            }
        };

        const seekForward = () => {
            const {currentTime} = getPlayerWatchTime(player);
            const nextPosition = Math.floor(
                currentTime + VideoPlayerConfig.SEEK_SECONDS,
            );
            state.setState({currentTime: nextPosition});
            seek(nextPosition);
        };

        const seekBack = () => {
            const {currentTime} = getPlayerWatchTime(player);
            const nextPosition = Math.floor(
                currentTime - VideoPlayerConfig.SEEK_SECONDS,
            );
            state.setState({currentTime: nextPosition});
            seek(nextPosition);
        };

        const volumeDown = () => {
            const currentVolume = player.volume() || 0;
            const targetVolume = Math.max(
                0,
                currentVolume - VideoPlayerConfig.VOLUME_CONTROL,
            );
            player.volume(targetVolume);
        };

        const volumeUp = () => {
            const currentVolume = player.volume() || 0;
            const targetVolume = Math.min(
                1,
                currentVolume + VideoPlayerConfig.VOLUME_CONTROL,
            );
            player.volume(targetVolume);
        };

        const setVolume = (volume: number) => {
            state.setState({volume});
            player.volume(volume);
        };

        const mute = (isMuted = true) => {
            player.muted(isMuted);
        };

        const toggleMute = () => {
            mute(!state.getState().isMuted);
        };

        const toggleFullscreen = () => {
            if (state.getState().isFullscreen) {
                exitFullscreen();
            } else {
                enterFullscreen();
            }
        };

        const enterFullscreen = () => {
            player.requestFullscreen();
        };

        const exitFullscreen = () => {
            player.exitFullscreen();
        };

        const toggleTheaterMode = (isTheaterMode?: boolean) => {
            state.setState({
                isTheaterMode: isTheaterMode ?? !state.getState().isTheaterMode,
            });
        };

        const toggleCC = () => {
            const i = state.getState().textTrackIndex;
            if (i === -1 || i >= player.textTracks().length) {
                Sentry.captureException(new Error('Invalid text track index'));
                return;
            }
            const wasEnabled = state.getState().ccEnabled;
            if (wasEnabled) {
                player.textTracks()[i].mode = 'disabled';
            } else {
                player.textTracks()[i].mode = 'showing';
            }
            state.setState({ccEnabled: !wasEnabled});
        };

        const addListener = (
            type: string,
            callback: (percentage: number, timestamp: number) => void,
        ) => {
            const shouldCallImmediately =
                type === VideoPlayerEventEnum.READY && state.getState().isReady;

            if (shouldCallImmediately) {
                const {progressPercentage, currentTime} =
                    getPlayerWatchTime(player);
                callback(progressPercentage, currentTime);
            }

            const listener = {type, callback};
            listeners.current.push(listener);
            return () => {
                const index = listeners.current.findIndex(l => listener === l);
                listeners.current.splice(index, 1);
            };
        };

        const callListener = (event: VideoPlayerEventEnum) => {
            const {progressPercentage, currentTime} =
                getPlayerWatchTime(player);
            listeners.current
                .filter(l => l.type === event)
                .forEach(l => l.callback(progressPercentage, currentTime));
        };

        const attachListeners = (player: Player) => {
            player.on(VideoJSEventEnum.PLAY, onPlay);
            player.on(VideoJSEventEnum.PAUSE, onPause);
            player.on(VideoJSEventEnum.ENDED, onEnded);
            player.on(VideoJSEventEnum.ERROR, onError);
            player.on(VideoJSEventEnum.TIME_UPDATE, onProgress);
            player.on(VideoJSEventEnum.VOLUME_CHANGE, onVolumeChange);
            player.on(VideoJSEventEnum.FULLSCREEN_CHANGE, onFullscreenChange);
            player.on(VideoJSEventEnum.LOADED_START, onLoadStart);
            player.on(VideoJSEventEnum.LOADED_DATA, onLoaded);
            player.on(VideoJSEventEnum.RATE_CHANGE, onRateChange);
            player.on(VideoJSEventEnum.SEEKING, onSeekStart);
            player.on(VideoJSEventEnum.SEEKED, onSeekEnd);
            player.on(VideoJSEventEnum.KEYDOWN, onPlayerKeyDown);
            player.on(VideoJSEventEnum.ENTER_PIP, onEnterPip);
            player.on(VideoJSEventEnum.LEAVE_PIP, onLeavePip);
            document.addEventListener('keydown', onDocumentKeyDown);
            player.qualityLevels().on('change', onQualityChange);
        };

        const onPlayerCreated = (newPlayer: Player) => {
            playerRef.current = newPlayer;
            attachListeners(newPlayer);
            state.setState({isReady: true});
            callListener(VideoPlayerEventEnum.READY);
        };

        const onQualityChange = () => {
            state.setState({
                qualityLevel:
                    player.qualityLevels()[player.qualityLevels().selectedIndex]
                        .height,
            });
        };

        const changePlaybackRate = (rate: number) => {
            player.playbackRate(rate);
        };

        const onRateChange = () => {
            state.setState({
                playbackRate: player.playbackRate(),
            });
        };

        const onPlay = () => {
            state.setState({
                isStarted: true,
                isPlaying: true,
                isEnded: false,
                error: false,
            });
            callListener(VideoPlayerEventEnum.PLAY);
        };

        const onLoadStart = () => {
            state.setState({isLoading: true});
            callListener(VideoPlayerEventEnum.LOAD_START);
        };

        const setControlsVisible = (visible: boolean) => {
            if (!visible) {
                EventBus.send({key: BusEventEnum.VIDEO_PLAYER_CLOSE_MENU});
            }
            state.setState({areControlsVisible: visible});
        };

        const onLoaded = () => {
            state.setState({
                isLoading: false,
                duration: getPlayerWatchTime(player).duration,
            });
            if (state.getState().isAutoplay) {
                play();
            }
            if (state.getState().isStarted && state.getState().startFrom) {
                // switching videos inside of single context
                seek(state.getState().startFrom);
            }

            const textTracks = player.textTracks();
            if (textTracks && textTracks.length > 0) {
                for (let i = 0; i < textTracks.length; i++) {
                    if (textTracks[i].kind === 'subtitles') {
                        state.setState({
                            textTrackIndex: i,
                            ccEnabled: textTracks[i].mode === 'showing',
                        });
                    }
                }
            }

            callListener(VideoPlayerEventEnum.LOADED);
        };

        const onPause = () => {
            state.setState({
                isPlaying: false,
            });

            callListener(VideoPlayerEventEnum.PAUSE);
        };

        const onEnded = () => {
            state.setState({
                isEnded: true,
                isPlaying: false,
            });
            callListener(VideoPlayerEventEnum.ENDED);
        };

        const onError = () => {
            state.setState({
                error: true,
                isPlaying: false,
            });
            callListener(VideoPlayerEventEnum.ERROR);
        };

        const onProgress = () => {
            state.setState({
                currentTime: player.currentTime() ?? 0,
                duration: player.duration() ?? 0,
            });

            callListener(VideoPlayerEventEnum.PROGRESS);
        };

        const onVolumeChange = () => {
            state.setState({
                volume: player.volume(),
                isMuted: player.muted(),
            });
            callListener(VideoPlayerEventEnum.VOLUME_CHANGE);
        };

        const onFullscreenChange = () => {
            state.setState({
                isFullscreen: player.isFullscreen() ?? false,
            });
            callListener(VideoPlayerEventEnum.FULLSCREEN_CHANGE);
        };

        const onScrubStart = () => {
            const {currentTime} = getPlayerWatchTime(player);
            state.setState({
                isScrubbing: true,
                isEnded: false,
                seekFrom: currentTime,
            });
            callListener(VideoPlayerEventEnum.SCRUB_START);
        };

        const onScrubEnd = () => {
            // delay seeking state update by one tick to avoid flashing play button
            // briefly when seeking to already buffered location.
            setTimeout(() => {
                state.setState({isScrubbing: false});
                callListener(VideoPlayerEventEnum.SCRUB_END);
            }, 0);
        };

        const onSeekStart = () => {
            state.setState({isSeeking: true});
            callListener(VideoPlayerEventEnum.SEEK_START);
        };

        const onSeekEnd = () => {
            state.setState({isSeeking: false});
            // scrub seeks are logged via SCRUB_END, skip the duplicate seeked
            if (seekIsFromScrubRef.current) {
                return;
            }
            callListener(VideoPlayerEventEnum.SEEK_END);
        };

        const onUIChange = (mutation: MutationRecord) => {
            const isInteracting = (
                mutation.target as HTMLDivElement
            )?.classList.contains('vjs-user-active');

            const currentState = state.getState();
            const wasInteracting = currentState.isInteracting;
            const wasPlaying = currentState.isPlaying;

            if (isInteracting && !wasInteracting) {
                state.setState({isInteracting: true});
            } else if (!isInteracting && wasInteracting && wasPlaying) {
                state.setState({isInteracting: false});
            }
        };

        const togglePictureInPicture = () => {
            if (player.isInPictureInPicture()) {
                player.exitPictureInPicture();
            } else {
                player.requestPictureInPicture();
            }
        };

        const onEnterPip = () => {
            state.setState({isInPip: true, areControlsVisible: false});
        };

        const onLeavePip = () => {
            state.setState({isInPip: false, areControlsVisible: true});
        };

        const documentKeyActions = {
            [KeyboardInputEnum.M]: toggleMute,
            [KeyboardInputEnum.Escape]: exitFullscreen,
            [KeyboardInputEnum.Space]: playPause,
            [KeyboardInputEnum.F]: toggleFullscreen,
        };

        const playerKeyActions = {
            [KeyboardInputEnum.ArrowLeft]: seekBack,
            [KeyboardInputEnum.ArrowUp]: volumeUp,
            [KeyboardInputEnum.ArrowRight]: seekForward,
            [KeyboardInputEnum.ArrowDown]: volumeDown,
        };

        const onPlayerKeyDown = createKeyboardEventHandler(playerKeyActions);

        const onDocumentKeyDown =
            createKeyboardEventHandler(documentKeyActions);

        const injectIntoVideoJsUI = (
            selector: string,
            injectableEl: HTMLElement,
        ) => {
            const target = player.el().querySelector(selector);
            if (!target) {
                throw Error(`No element found for selector: ${selector}`);
            }
            target.appendChild(injectableEl);
        };

        const getPreservedState = () => {
            return {
                volume: state.getState().volume,
                isMuted: state.getState().isMuted,
            };
        };

        const cleanup = () => {
            state.setState({...initState, ...getPreservedState()});
            if (player) {
                Object.values(VideoJSEventEnum).forEach(event => {
                    player.off(event as string);
                });
                player.dispose();
                playerRef.current = null;
            }
            document.removeEventListener('keydown', onDocumentKeyDown);
        };

        const cleanupTransient = () => {
            // reset state values without triggering react re-renders by
            // mutating state object directly
            Object.entries({...initState, ...getPreservedState()}).forEach(
                ([key, value]) => {
                    state.getState()[key as keyof IVideoPlayerState] =
                        value as never;
                },
            );
        };

        return {
            _state: state,
            _onPlayerCreated: onPlayerCreated,
            _onUIChange: onUIChange,
            _onSeekStart: onSeekStart,
            _onSeekEnd: onSeekEnd,
            _onScrubStart: onScrubStart,
            _onScrubEnd: onScrubEnd,
            _cleanupTransient: cleanupTransient,
            _cleanup: cleanup,
            _player: player,
            _injectIntoVideoJsUI: injectIntoVideoJsUI,
            load,
            mute,
            toggleMute,
            seek,
            toggleTheaterMode,
            toggleCC,
            play,
            pause,
            playPause,
            enterFullscreen,
            exitFullscreen,
            volumeDown,
            volumeUp,
            changePlaybackRate,
            setVolume,
            togglePictureInPicture,
            seekBack,
            seekForward,
            addListener,
            setControlsVisible,
        };
    }, [state]);

    return controller;
};

const VideoPlayerContext = createContext<ReturnType<
    typeof useCreateVideoController
> | null>(null);

export const VideoPlayerProvider: FC<PropsWithChildren> = ({children}) => {
    const video = useCreateVideoController();

    return (
        <VideoPlayerContext.Provider value={video}>
            {children}
        </VideoPlayerContext.Provider>
    );
};

export const withVideoPlayer = <P extends object>(WrappedComponent: FC<P>) => {
    function ComponentWithPlayerContext(props: P) {
        const context = useContext(VideoPlayerContext);
        if (context) {
            return <WrappedComponent {...props} />;
        }

        return (
            <VideoPlayerProvider>
                <WrappedComponent {...props} />
            </VideoPlayerProvider>
        );
    }

    return ComponentWithPlayerContext;
};

export const useVideoPlayer = () => {
    const context = useContext(VideoPlayerContext);
    if (!context) {
        throw Error('Missing context for video player');
    }
    return context;
};

export const useVideoPlayerState = <K,>(
    selector: (s: IVideoPlayerState) => K,
) => {
    const context = useContext(VideoPlayerContext);
    if (!context) {
        throw Error('Missing context for video player');
    }
    return useStore(context._state, selector);
};
